import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Fuel, Droplets, Store, Wrench, Truck, Shield, Award, Clock } from 'lucide-react';
import { services } from '../data/mock';
import { useTranslation } from 'react-i18next';

const Services = () => {
  const { t } = useTranslation();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">{t('services.title')}</h1>
            <p className="text-xl text-red-100 leading-relaxed">
              {t('services.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                      {service.icon === 'Fuel' && <Fuel className="w-6 h-6 text-red-600" />}
                      {service.icon === 'Droplets' && <Droplets className="w-6 h-6 text-red-600" />}
                      {service.icon === 'Store' && <Store className="w-6 h-6 text-red-600" />}
                      {service.icon === 'Wrench' && <Wrench className="w-6 h-6 text-red-600" />}
                      {service.icon === 'Truck' && <Truck className="w-6 h-6 text-red-600" />}
                    </div>
                    <h3 className="text-2xl font-bold">
                      {service.id === 1 && t('services.fuel.title')}
                      {service.id === 2 && t('services.market.title')}
                      {service.id === 3 && t('services.carwash.title')}
                      {service.id === 4 && t('services.technical.title')}
                      {service.id === 5 && t('services.special.title')}
                    </h3>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {service.id === 1 && t('services.fuel.description')}
                    {service.id === 2 && t('services.market.description')}
                    {service.id === 3 && t('services.carwash.description')}
                    {service.id === 4 && t('services.technical.description')}
                    {service.id === 5 && t('services.special.description')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Car Wash Details */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Droplets className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="text-3xl font-bold">{t('services.carwashSection')}</h2>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {t('services.carwashDesc')}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-semibold mb-1">{t('services.carwashExternal')}</h4>
                      <p className="text-gray-600 text-sm">{t('services.carwashExternalDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-semibold mb-1">{t('services.carwashQuality')}</h4>
                      <p className="text-gray-600 text-sm">{t('services.carwashQualityDesc')}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                    <div>
                      <h4 className="font-semibold mb-1">{t('services.carwashFast')}</h4>
                      <p className="text-gray-600 text-sm">{t('services.carwashFastDesc')}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="h-96 rounded-lg overflow-hidden shadow-xl">
                <img
                  src="https://images.pexels.com/photos/6873191/pexels-photo-6873191.jpeg"
                  alt="Avtoyuma"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fuel Quality Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">{t('services.qualityTitle')}</h2>
              <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
                {t('services.qualityDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Fuel className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{t('services.ai92Title')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.ai92Desc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Fuel className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{t('services.ai95Title')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.ai95Desc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Fuel className="w-8 h-8 text-yellow-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{t('services.dizelTitle')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.dizelDesc')}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">{t('services.whyChooseTitle')}</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('services.certifiedQuality')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.certifiedQualityDesc')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('services.intlBrand')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.intlBrandDesc')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('services.reliableService')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.reliableServiceDesc')}
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Store className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t('services.extraComfort')}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('services.extraComfortDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
